import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Eye, Calendar, Share2, ArrowLeft, User, Moon, Sun } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import { getImageUrl } from '../../utils/imageUtils';
import CommentSection from '../../components/blog/CommentSection';
import { sanitizeHtml } from '../../utils/sanitize';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [navigation, setNavigation] = useState({ previous: null, next: null });
  const [loading, setLoading] = useState(true);
  const { theme, toggleTheme } = useTheme();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      console.log('Fetching post:', slug);
      
      const [postRes, relatedRes, navigationRes] = await Promise.all([
        fetch(`${API_BASE}/api/blog/posts/${slug}`).then(r => r.json()),
        fetch(`${API_BASE}/api/blog/posts/${slug}/related?limit=6`).then(r => r.json()),
        fetch(`${API_BASE}/api/blog/posts/${slug}/navigation`).then(r => r.json())
      ]);
      
      console.log('Post fetched:', postRes);
      console.log('Related posts:', relatedRes);
      console.log('Navigation:', navigationRes);
      
      setPost(postRes);
      setRelatedPosts(relatedRes || []);
      setNavigation(navigationRes || { previous: null, next: null });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching post:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShare = async () => {
    const shareData = {
      title: post?.title || 'Blog Post',
      text: post?.excerpt || post?.title || 'Check out this blog post!',
      url: window.location.href
    };

    try {
      if (navigator.share) {
        // Use native share on mobile
        await navigator.share(shareData);
      } else {
        // Fallback: Copy URL to clipboard
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback: Copy URL to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 dark:border-purple-400"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 dark:text-gray-100">Article not found</h2>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'}
                alt="TrainerList" 
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold">Home</Link>
              <Link to="/blog" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black font-bold">Blog</Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/blog" className="inline-flex items-center text-red-700 dark:text-red-400 hover:text-pink-600 dark:hover:text-pink-400 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {/* Hero Image */}
        <div className="aspect-video rounded-3xl overflow-hidden mb-8 shadow-2xl bg-white dark:bg-gray-900">
          <img 
            src={getImageUrl(post.cover_image)} 
            alt={post.title} 
            className="w-full h-full object-contain" 
          />
        </div>

        {/* Title & Meta */}
        <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight dark:text-gray-100">{post.title}</h1>
        
        <div className="flex items-center gap-6 mb-8 pb-8 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            {post.author && (
              <>
                <Avatar>
                  <AvatarImage src={post.author.avatar} />
                  <AvatarFallback className="dark:bg-gray-700 dark:text-gray-300">{post.author.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold dark:text-gray-200">{post.author.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{post.author.role}</p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(post.published_at || post.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views} views
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none mb-12 dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
        />

        {/* Author Bio */}
        {post.author && (
          <Card className="mb-12 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-gray-800 dark:to-gray-700 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={post.author.avatar} />
                  <AvatarFallback className="dark:bg-gray-700 dark:text-gray-300">{post.author.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-bold text-lg mb-1 dark:text-gray-100">About {post.author.name}</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">{post.author.bio}</p>
                  <div className="flex gap-3">
                    {post.author.social_links?.instagram && (
                      <a href={post.author.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 hover:text-pink-600 dark:hover:text-pink-400">
                        Instagram
                      </a>
                    )}
                    {post.author.social_links?.twitter && (
                      <a href={post.author.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 hover:text-pink-600 dark:hover:text-pink-400">
                        Twitter
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post Navigation - Previous/Next */}
        {(navigation.previous || navigation.next) && (
          <div className="my-12 border-t border-b border-gray-200 dark:border-gray-700 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Previous Post */}
              {navigation.previous ? (
                <Link 
                  to={`/blog/post/${navigation.previous.slug}`}
                  className="group flex items-center gap-4 p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-xl hover:shadow-lg transition-all border border-purple-100 dark:border-gray-600"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ArrowLeft className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-red-700 dark:text-red-400 font-semibold mb-1">Previous Post</p>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                      {navigation.previous.title}
                    </h3>
                  </div>
                </Link>
              ) : (
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-xl opacity-50">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No previous post</p>
                </div>
              )}

              {/* Next Post */}
              {navigation.next ? (
                <Link 
                  to={`/blog/post/${navigation.next.slug}`}
                  className="group flex items-center gap-4 p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-800 rounded-xl hover:shadow-lg transition-all border border-blue-100 dark:border-gray-600 text-right"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold mb-1">Next Post</p>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {navigation.next.title}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ArrowLeft className="w-6 h-6 rotate-180" />
                  </div>
                </Link>
              ) : (
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-xl opacity-50 text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No next post</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-6 dark:text-gray-100">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Card key={related.id} className="overflow-hidden hover:shadow-xl transition-all transform hover:scale-105 dark:bg-gray-800 dark:border-gray-700">
                  <CardContent className="p-0">
                    <div className="aspect-video bg-gray-100 dark:bg-gray-900">
                      <img src={getImageUrl(related.cover_image)} alt={related.title} className="w-full h-full object-contain" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold mb-2 line-clamp-2 hover:text-red-600 dark:hover:text-red-400 dark:text-gray-100">
                        <Link to={`/blog/post/${related.slug}`}>{related.title}</Link>
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{related.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{related.author_name}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {related.reading_time} min
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Comment Section */}
        {post && (
          <div className="max-w-4xl mx-auto px-4">
            <CommentSection postSlug={slug} />
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;